import { ComponentObservable, componentObserver } from '../../component-observer';
import { currentBreakpoint } from '../../utils/resize.utils';

/**
 * Module for handling page title responsive behavior, specifically moving the back button
 * to the controls area on mobile devices.
 */
class PageTitleModule implements ComponentObservable {
  /**
   * Selector of all page title components that must be initialized in javascript.
   */
  componentSelector = '.page-title';

  /**
   * Initialize all page title components on the current page.
   * @param observe Boolean if the pageTitle module should listen for changes in the DOM and initialize dynamically
   *   added page title components
   */
  initializeAll(observe: boolean): Promise<void> {
    return new Promise((resolve) => {
      const allPageTitleComponents = document.querySelectorAll(this.componentSelector);
      for (let i = 0; i < allPageTitleComponents.length; i++) {
        this.initialize(allPageTitleComponents[i] as HTMLElement);
      }

      if (observe) {
        this.startListening();
      }
      resolve();
    });
  }

  /**
   * Listen for changes in DOM and initialize page title components when new ones appear
   */
  startListening(): void {
    componentObserver.subscribeListener(this);
  }

  /**
   * Initialize the given page title element.
   * @param pageTitle The page title root element that should be initialized
   */
  initialize(pageTitle: HTMLElement): void {
    componentObserver.markElementAsInitialized(this.componentSelector, pageTitle);

    const backButton = pageTitle.querySelector('.resources-detail-back-button') as HTMLElement;
    const controls = pageTitle.querySelector('.page-title__controls') as HTMLElement;

    if (!backButton || !controls) {
      return; // No back button or controls found, nothing to do
    }

    // Set up responsive behavior
    this.setupResponsiveBehavior(pageTitle, backButton, controls);

    // Listen for breakpoint changes
    window.addEventListener('breakpoint-changed', () => {
      this.setupResponsiveBehavior(pageTitle, backButton, controls);
    });
  }

  /**
   * Sets up the responsive behavior for the page title component
   * @param pageTitle The page title root element
   * @param backButton The back button element
   * @param controls The controls container element
   */
  private setupResponsiveBehavior(pageTitle: HTMLElement, backButton: HTMLElement, controls: HTMLElement): void {
    const breakpoint = currentBreakpoint();

    if (breakpoint.isDesktop) {
      this.setupDesktopLayout(pageTitle, backButton, controls);
    } else {
      this.setupMobileLayout(backButton, controls);
    }
  }

  /**
   * Sets up the mobile layout by moving the back button to controls and wrapping the controls that must remain on the right
   * @param backButton The back button element
   * @param controls The controls container element
   */
  private setupMobileLayout(backButton: HTMLElement, controls: HTMLElement): void {
    // Check if we've already set up the mobile layout
    if (controls.querySelector('.resources-detail-back-button')) {
      return; // Already set up
    }

    // Clone the back button for mobile
    const mobileBackButton = backButton.cloneNode(true) as HTMLElement;

    // Create a wrapper for the existing controls (share and print buttons)
    const controlsRight = document.createElement('div');
    controlsRight.className = 'page-title__controls-right';

    // Move existing controls into the right wrapper
    while (controls.firstChild) {
      controlsRight.appendChild(controls.firstChild);
    }

    // Add the mobile back button first, then the controls wrapper
    controls.appendChild(mobileBackButton);
    controls.appendChild(controlsRight);
  }

  /**
   * Sets up the desktop layout by removing the mobile back button and unwrapping controls
   * @param pageTitle The page title root element
   * @param backButton The original back button element
   * @param controls The controls container element
   */
  private setupDesktopLayout(pageTitle: HTMLElement, backButton: HTMLElement, controls: HTMLElement): void {
    // Remove the mobile back button if it exists
    const mobileBackButton = controls.querySelector('.resources-detail-back-button');
    if (mobileBackButton) {
      mobileBackButton.remove();
    }

    // Unwrap the controls-right container if it exists
    const controlsRight = controls.querySelector('.page-title__controls-right');
    if (controlsRight) {
      // Move children back to controls
      while (controlsRight.firstChild) {
        controls.appendChild(controlsRight.firstChild);
      }
      controlsRight.remove();
    }

    // Ensure the original back button is visible (it might have been hidden by CSS)
    backButton.style.display = '';
  }
}

const pageTitleModule = new PageTitleModule();
export { pageTitleModule as pageTitle }; 